import React, { useState } from 'react';
import Section from './Section';
import AttentionList from '../../components/dashboard/AttentionList';
import RFQTable from '../../components/dashboard/RFQTable';
import { needsBuyerReview } from '../../hooks/useDashboard';
import type { AttentionItem, DashboardRFQ, StageFilter, OwnerView } from '../../hooks/useDashboard';

/**
 * /design gallery — dashboard attention queue + Active RFQs table states.
 * Compare with the bottom half of `design/Dashboard v2.dc.html`. Sample
 * data mirrors the mock's rows (incl. the Robbie insight chip slot, populated
 * for real by the insights task later); the live dashboard renders only real
 * fetched state. Awarded RFQs appear in neither the queue nor the table —
 * "Active RFQs" is active-only by definition (owner decisions, 28 Jul).
 */

const hoursAgo = (h: number) => new Date(Date.now() - h * 3_600_000).toISOString();

const attention: AttentionItem[] = [
    { id: 'RFQ-142', name: 'Warehouse AMR Drive Subsystem', reason: 'quotes-review', label: 'Award recommendation ready to review', actionLabel: 'Review', insight: 'lowest bid risks your Q4 date' },
    { id: 'RFQ-139', name: 'Autonomous Drone Propulsion Kit', reason: 'supplier-replies', label: '2 supplier replies to review', actionLabel: 'Open' },
    { id: 'RFQ-131', name: 'Palletizer Vision Retrofit', reason: 'quotes-open', label: 'Quotes stage open — award plan not started', actionLabel: 'Open' },
];

const row = (
    id: string, name: string | null, stageStep: number, awarded: boolean,
    owner: string | null, partsCount: number, updatedHoursAgo: number, repliesCount = 0,
): DashboardRFQ => ({
    id, name, stageStep, awarded, owner, partsCount, repliesCount,
    stage: (['Upload BOM', 'Sourcing Criteria', 'Supplier Discovery', 'Supplier Outreach', 'Quotes & Proposals'] as const)[stageStep - 1],
    status: awarded ? 'AWARDED' : 'DRAFT',
    isMine: owner === 'You',
    recommendationReady: !awarded && stageStep === 5,
    pendingOnEndUser: false,
    endUserLabel: null,
    iAmEndUser: false,
    createdAtIso: hoursAgo(updatedHoursAgo + 240),
    updatedAtIso: hoursAgo(updatedHoursAgo),
});

const rows: DashboardRFQ[] = [
    row('RFQ-142', 'Warehouse AMR Drive Subsystem', 5, false, 'You', 34, 48),
    row('RFQ-139', 'Autonomous Drone Propulsion Kit', 4, false, 'Marcus T.', 18, 6, 2),
    row('RFQ-137', '6-Axis Arm Harmonic Actuators', 3, false, 'You', 22, 24),
    row('RFQ-135', 'Humanoid Hand Sensor Array', 2, false, 'Alex R.', 41, 72),
    row('RFQ-134', null, 1, false, null, 9, 120), // unnamed → muted "Untitled project", no stuttered id
];

const noop = () => undefined;

/** Mirrors the dashboard hook's filter rules over the sample rows. */
const InteractiveTable: React.FC = () => {
    const [ownerView, setOwnerView] = useState<OwnerView>('everyone');
    const [stageFilter, setStageFilter] = useState<StageFilter>('all');

    const inView = rows.filter((r) => !r.awarded && (ownerView === 'everyone' || r.isMine));
    const visible = inView.filter((r) => {
        if (stageFilter === 'all') return true;
        if (stageFilter === 'needs-review') return needsBuyerReview(r);
        return r.stage === stageFilter && !r.awarded;
    });

    return (
        <RFQTable
            rows={visible}
            ownerView={ownerView}
            onOwnerViewChange={setOwnerView}
            stageFilter={stageFilter}
            onStageFilterChange={setStageFilter}
            counts={{
                all: inView.length,
                needsReview: inView.filter(needsBuyerReview).length,
            }}
            onRowClick={noop}
        />
    );
};

const emptyProps = {
    ownerView: 'mine' as OwnerView, onOwnerViewChange: noop,
    stageFilter: 'all' as StageFilter, onStageFilterChange: noop,
    counts: { all: 0, needsReview: 0 }, onRowClick: noop,
};

const DashboardListGallery: React.FC = () => (
    <Section title="Dashboard — attention queue + RFQ table">
        <div className="flex flex-col gap-s6">
            <div className="flex flex-col gap-s3">
                <p className="text-meta text-ink-muted">Attention queue (real reasons only; row 1 shows the Robbie insight chip slot; awarded RFQs never appear)</p>
                <AttentionList items={attention} onAction={noop} />
            </div>
            <div className="flex flex-col gap-s3">
                <p className="text-meta text-ink-muted">Active RFQs — owner/stage filters + sort live; unnamed row falls back to a muted placeholder; awarded RFQs never render here</p>
                <InteractiveTable />
            </div>
            <div className="flex flex-col gap-s3">
                <p className="text-meta text-ink-muted">Loading skeleton</p>
                <RFQTable rows={[]} {...emptyProps} loading />
            </div>
            <div className="flex flex-col gap-s3">
                <p className="text-meta text-ink-muted">Honest empty state (My projects, nothing active)</p>
                <RFQTable rows={[]} {...emptyProps} />
            </div>
        </div>
    </Section>
);

export default DashboardListGallery;
